import { Fragment, useId } from 'react'
import { PANEL_HIT_CLASS, useHitTarget, type HitTargetSize } from '../responsive/hitTarget'
import './segmentedControl.css'

export type Segment<T extends string> = {
  value: T
  label: string
  /** How many the segment holds, drawn as a small pill after the label and
   *  read as "Label, N". Omit it and the segment renders exactly as it always
   *  has; `0` is a count, only `undefined` means "not known". */
  count?: number
  /** This one option cannot be chosen. The others still can. Native `disabled`:
   *  it leaves the tab order, so prefer `unavailable` where a person should be
   *  able to find the option and hear why it is off. */
  disabled?: boolean
  /** Declared-disabled, with the reason. The option cannot be chosen, but stays
   *  focusable and reachable by the arrow keys; it is `aria-disabled` and its
   *  reason is read at focus through `aria-describedby` (the shape
   *  `DisabledReason` documents). Click, Space and Enter are ignored. */
  unavailable?: string
}

/**
 * `.segmented`'s own vertical padding (`segmentedControl.css`) and border
 * width (`segmentedControl.css`, `border: 1px solid`) — both feed the radius
 * formula below and are pinned against the stylesheet by
 * `SegmentedControl.test.tsx` (B3, architect review #407), so an edit to
 * either constant OR the CSS values it mirrors is caught rather than
 * silently drifting the wrap shape.
 */
const SEGMENTED_PADDING = 3
const SEGMENTED_BORDER = 1

/**
 * One-of-N choice as a pill group — a filter, a sort order, a page size.
 *
 * ## Why this exists
 *
 * Seven of these had been written by hand across five stylesheets before it
 * did: the merchants type filter and its rows-per-page control, the categories
 * sort, and three segments inside the two forms. That is the third-progress-ring
 * mistake this repo's brief records from the iOS codebase, and the drift was
 * already visible — the sort group shipped `--chip`/`--edge` while the type
 * filter shipped `--surface`/`--ink2`, so two controls doing the same job on
 * the same page did not match.
 *
 * ## Radios, never a `<select>`, never bare buttons
 *
 * Real `<input type="radio">` elements, visually hidden. A native `<select>`
 * draws its closed box and its option list in OS chrome, and neither carries a
 * declaration a colour or contrast sweep can read — an absent declaration is
 * unscannable, and this repo has shipped that defect twice. Radios give
 * Tab-into-the-group and arrow-between-options for free, and every pixel a
 * user or a sweep sees is a class in this file's stylesheet.
 *
 * `role="radiogroup"` and not `role="group"` with `aria-pressed` buttons: the
 * choice is exclusive, and a radiogroup announces "2 of 5" where a set of
 * toggle buttons announces five independent pressed states.
 *
 * ## `name` must be unique per group on a page
 *
 * Radios with the same `name` form ONE exclusive group in the DOM, so two
 * `SegmentedControl`s sharing a name would deselect each other. It is a
 * required prop rather than one derived from `label`, because a derived name
 * would collide silently the day two groups are labelled the same.
 *
 * ## The floor applies to WIDTH too, not only height (issue #407)
 *
 * `useHitTarget` raised the item's `min-height` below 640px, but nothing
 * raised its `min-width` — so a narrow label (`merchants`' "Rows per page"
 * options are two-digit numerals) can clear the height floor and still miss
 * the width one. `make test-browser`'s hit-targets sweep measured exactly
 * that: `input.segmented__input` at `/merchants @ 375px`, 38.9×44 and
 * 40.5×44 against a 44px floor. `minWidth` now mirrors `minHeight` — the same
 * `target`, both axes. `segmentedControl.css`'s `.segmented__item` gets
 * `justify-content: center` for exactly the same reason (architect review
 * B1): stretching a narrower box up to the floor puts all the slack on one
 * side with no centring, so the label visibly left-shifts — inside the lime
 * `--accent` fill when selected.
 *
 * ## The capsule radius tracks ONE row, not the wrapped box (issue #407)
 *
 * `.segmented` wraps by design (`flex-wrap: wrap` in `segmentedControl.css`)
 * — a group can carry more options than one row comfortably fits (the
 * merchants type filter is six, at 375px). `border-radius` is a property of
 * the WHOLE box, and a browser clamps a value larger than half the box's own
 * height down to exactly that — so `var(--r-pill)`'s 99px, generous enough to
 * fully round a single row, balloons into a stretched stadium the moment a
 * second row appears, because the SAME clamp then measures against the
 * taller wrapped box instead. No stylesheet can express "half of one row" on
 * its own, because CSS has no unit relative to a single flex line rather than
 * the box's own (possibly-wrapped) height — that one number can only come
 * from here.
 *
 * **The number is `target / 2 + SEGMENTED_PADDING + SEGMENTED_BORDER`, not
 * `target / 2 + SEGMENTED_PADDING` alone** (architect review B2): the clamp
 * measures the BORDER box, and `.segmented` carries `border: 1px solid` in
 * addition to its padding, so one row's border-box height is
 * `target + 2×padding + 2×border` and a full capsule needs half of that. The
 * padding-only formula undershoots by exactly the border width — a hairline
 * flat segment at each end's vertical midpoint, invisible to
 * `make test-browser` (which measures the input's box, not the capsule's
 * corner) but real. With the border term included this lands on the SAME
 * value the browser was already clamping `var(--r-pill)` to for a single
 * row — a no-op for any caller whose row height is the hit target; a caller
 * whose label wraps within an item gets a rounded rect rather than a
 * capsule, which is intended. For a wrapped-to-a-second-ROW box it caps the
 * corners to a proper rounded rectangle instead of a stadium.
 *
 * **The declaration itself still lives in CSS** (rule 2 — "skins live in
 * CSS"): this component only sets the `--segmented-row-radius` custom
 * property; `segmentedControl.css` owns `border-radius:
 * var(--segmented-row-radius, var(--r-pill))`, the same `--swatch` pattern
 * `MerchantAvatar` already uses for a per-instance value a stylesheet cannot
 * precompute on its own.
 */
export function SegmentedControl<T extends string>({
  label,
  name,
  options,
  value,
  onChange,
  size = 'page',
  tone = 'accent',
  disabled = false,
  describedBy,
}: {
  /** The group's accessible name — announced before the options. */
  label: string
  /** Unique per group on the page; see the docblock. */
  name: string
  options: readonly Segment<T>[]
  value: T
  onChange: (value: T) => void
  /** `page` (33px) by default — every call site before issue #395 drew a
   *  toolbar filter or sort at that density. `form` is the pre-login/dialog
   *  density (`Field`'s own `size` prop makes the same distinction), for a
   *  segment sitting beside `Field`s inside a modal rather than in a page
   *  toolbar. No existing call site passes this, so none of them change. */
  size?: HitTargetSize
  /** `accent` by default — the lime selected item every caller has drawn.
   *  `neutral` is the artboard's quiet segment: a borderless `--chip` track
   *  and a `--surface` selected pill, for a group inside a content area whose
   *  lime fill already belongs to its primary action (rule 9). Use it on an
   *  opaque surface; see segmentedControl.css. `outline` is the artboard's month
   *  pills: no track, and the selected option wears an `--accent-ink` outline
   *  and weight instead of a lime fill, for a group in a content area whose
   *  lime fill belongs elsewhere. */
  tone?: 'accent' | 'neutral' | 'outline'
  /** The whole control cannot be changed. Each radio is natively disabled (it
   *  blocks the change and is announced as dimmed), and the group says so too.
   *  Pair it with `describedBy` so a person who reaches it hears why. */
  disabled?: boolean
  /** The id of the text that explains the control, on the radiogroup. */
  describedBy?: string
}) {
  const target = useHitTarget(size)
  const rowRadius = target / 2 + SEGMENTED_PADDING + SEGMENTED_BORDER
  const reasonIdBase = useId()

  return (
    <span
      className={tone === 'accent' ? 'segmented' : `segmented segmented--${tone}`}
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      aria-describedby={describedBy}
      style={{ '--segmented-row-radius': `${rowRadius}px` } as React.CSSProperties}
    >
      {options.map((option, i) => {
        const unavailable = option.unavailable !== undefined
        const reasonId = `${reasonIdBase}-${i}`
        return (
          <Fragment key={option.value}>
          <label
            className={[
              'segmented__item',
              disabled || option.disabled === true || unavailable ? 'segmented__item--disabled' : '',
              size === 'panel' ? PANEL_HIT_CLASS : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ minHeight: target, minWidth: target }}
          >
            <input
              className={size === 'panel' ? `segmented__input ${PANEL_HIT_CLASS}` : 'segmented__input'}
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={disabled || option.disabled === true}
              onChange={() => {
                if (!unavailable) onChange(option.value)
              }}
              aria-disabled={unavailable || undefined}
              aria-describedby={unavailable ? reasonId : undefined}
              // A declared-disabled radio must not take Enter, which would submit
              // an enclosing form. A click needs no guard: `checked` is controlled.
              onKeyDown={
                unavailable
                  ? (e) => {
                      if (e.key === 'Enter') e.preventDefault()
                    }
                  : undefined
              }
              // Only with a count: the label's own text would run the two
              // together ("Rules6"), so the name is given the pause.
              aria-label={option.count === undefined ? undefined : `${option.label}, ${option.count}`}
            />
            <span className="segmented__text">
              {option.label}
              {option.count !== undefined && <span className="segmented__count">{option.count}</span>}
            </span>
          </label>
            {/* Outside the label, or it would join the radio's accessible name. */}
            {unavailable && (
              <span id={reasonId} className="sr-only">
                {option.unavailable}
              </span>
            )}
          </Fragment>
        )
      })}
    </span>
  )
}
